import {
  useInteractions,
  type Placement,
  useFloating,
  autoUpdate,
  useDismiss,
  offset,
  shift,
  flip,
  size,
} from '@floating-ui/react';
import React from 'react';

import { createContextFactory } from '../../context';
import { useResponsive } from '../../hooks';

export interface DropdownContextProps {
  children: React.ReactNode;
  /**
   * Indicates that dropdown is open by default.
   */
  defaultOpen?: boolean;
  /**
   * Indicates position of dropdown relative to anchor element.
   */
  placement?: Placement;
  /**
   * Callback triggered when dropdown is closed.
   */
  onClose?: () => void;
  /**
   * Callback triggered when dropdown is opened.
   */
  onOpen?: () => void;
}

export type DropdownContextType = {
  /**
   * Passed down to Ref component to get reference props.
   */
  getReferenceProps: ReturnType<typeof useInteractions>['getReferenceProps'];
  /**
   * Passed down to Floating component to get floating props.
   */
  getFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps'];
  floatingStyles: ReturnType<typeof useFloating>['floatingStyles'];
  /**
   * Passed down to `ref` property of anchor element.
   */
  setAnchor: React.Dispatch<React.SetStateAction<Element | null>>;
  /**
   * Used to set open state of dropdown.
   */
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  strategy: ReturnType<typeof useFloating>['strategy'];
  context: ReturnType<typeof useFloating>['context'];
  refs: ReturnType<typeof useFloating>['refs'];
  /**
   * Placement of dropdown relative to anchor element.
   */
  placement: Placement;
  /**
   * Indicates if dropdown is currently open.
   */
  open: boolean;
};

const DropdownContext = createContextFactory({
  devToolsName: 'DropdownContext',
  initialValue: {} as DropdownContextType,
});

const DropdownProvider = ({
  children,
  defaultOpen = false,
  onClose,
  onOpen,
  placement = 'bottom-start',
}: DropdownContextProps) => {
  const [open, setOpen] = React.useState(defaultOpen);
  const [anchor, setAnchor] = React.useState<Element | null>(null);
  const [actualPlacement, setActualPlacement] =
    React.useState<Placement>(placement);

  const isMobile = useResponsive('down', 'sm');

  const useFloatingArgs = React.useMemo(() => {
    const middleware = [
      offset({ mainAxis: 8 }),
      flip(),
      shift({ crossAxis: true }),
      size({
        apply({ elements, rects }) {
          if (isMobile) return;
          // Use content width, with a fallback to a default width
          const contentWidth = Math.max(rects.floating.width, 400); // 400px as default width
          const maxWidth =
            rects.reference.width < 400 ? 400 : rects.reference.width;
          elements.floating.style.width = `${Math.min(contentWidth, maxWidth)}px`;
        },
      }),
    ];

    return {
      elements: {
        reference: anchor,
      },
      middleware,
      onOpenChange: (isOpen: boolean) => {
        setOpen(isOpen);
        if (isOpen && onOpen) onOpen();
        if (!isOpen && onClose) onClose();
      },
      open,
      placement: placement || 'bottom-end',
      whileElementsMounted: autoUpdate,
    } as Parameters<typeof useFloating>[0];
  }, [anchor, isMobile, onClose, onOpen, open, placement]);

  const { context, floatingStyles, refs, strategy } =
    useFloating(useFloatingArgs);
  const handleDismiss = useDismiss(context);

  const useInteractionsArgs = React.useMemo(() => {
    return [handleDismiss];
  }, [handleDismiss]);

  const { getFloatingProps, getReferenceProps } =
    useInteractions(useInteractionsArgs);

  React.useEffect(() => {
    if (open) setActualPlacement(context.placement);
  }, [open, context.placement]);

  const providedValue = React.useMemo(
    () => ({
      context,
      floatingStyles,
      getFloatingProps,
      getReferenceProps,
      open,
      placement: actualPlacement,
      refs,
      setAnchor,
      setOpen,
      strategy,
    }),
    [
      strategy,
      floatingStyles,
      context,
      refs,
      getReferenceProps,
      getFloatingProps,
      setOpen,
      setAnchor,
      open,
      actualPlacement,
    ],
  );
  return (
    <DropdownContext.Provider value={providedValue}>
      {children}
    </DropdownContext.Provider>
  );
};

export function useDropdownContext() {
  return DropdownContext.useContext();
}
export default DropdownProvider;
